import { ACTION_FETCH_FEATURE_FLAGS, fetchFeatureFlags } from '../../configs/featureFlagSlice'
import { RequestState, getAction, getShowLoader } from '../../reducers/requestReducer'
import { RootState } from '../../rootReducer'
import { useAuth } from '../../HOCs/AuthProvider'
import { ACTIONS } from '../PermissionMapper/constants'
import { PermissionProviderProps, usePermission } from '../../HOCs/PermissionProvider'
import LoaderView from '../Loader/loader'
import { setLanguage } from '../../features/manageAccount/languageSlice'
import { setUserDetails } from '../../features/login/loginSlice'
import { accessTokenKey, DEFAULT_LANGUAGE, URL_DASHBOARD, URL_UNAUTHORIZED } from '../../constants'
import { useThemeContext } from '../../features/dashboards/mainDashboardv2/components/ThemeContext'
import { STATES } from '../../interfaces'
import Navbar from '../Navbar'
import ServerError from '../ServerError'
import { getSessionStateAndCode } from '../../features/loading/loading'
import { fetchClassificationFlag } from '../../configs/classificationEngineSlice'
import { goToLoginPage } from '../../utils/urlUtil'
import React, { useEffect, useMemo } from 'react'
import { connect, useSelector } from 'react-redux'
import { Route, Redirect, useLocation } from 'react-router-dom'

interface IProps {
  exact?: boolean
  strict?: boolean
  path: string | string[]
  date?: string
  hideNavBar?: boolean
  component: React.ComponentType<any>
  children?: React.ReactNode
  redirectPath?: string
  requireAuth?: boolean
  fetchFeatureFlagsAction?: RequestState
  setLanguage: (value: string) => void
  setUserDetails: (userDetails: any) => void
  fetchFeatureFlags: () => void
  fetchClassificationFlag: () => void
  action?: ACTIONS
  isAuth: boolean
}

const ProtectedRoute = ({
  path,
  exact,
  strict,
  date,
  hideNavBar = false,
  component: Component,
  children,
  requireAuth = true,
  fetchFeatureFlagsAction,
  setLanguage,
  setUserDetails,
  fetchFeatureFlags,
  fetchClassificationFlag,
  action,
  isAuth = true
}: IProps) => {
  const { sessionState, code } = getSessionStateAndCode()

  const location = useLocation()
  const { isLoadingUserInfo: authLoading, userDetails } = useAuth()
  const { hasNavAction, getLandingUrl } = usePermission() as PermissionProviderProps
  const { theme } = useThemeContext()

  const showLoader = useSelector(() => authLoading || getShowLoader(ACTION_FETCH_FEATURE_FLAGS))
  const serverError = useSelector((state: RootState) => state.user.serverError)
  const hasLocalToken = !!window.localStorage.getItem(accessTokenKey)

  const showServerError = useMemo(() => {
    return serverError && !showLoader
  }, [serverError, showLoader])

  useEffect(() => {
    if (userDetails?.language) {
      setLanguage(userDetails?.language || DEFAULT_LANGUAGE)
    }
    if (isAuth && requireAuth && !!userDetails) {
      fetchFeatureFlags()
      fetchClassificationFlag()
      setUserDetails(userDetails)
    }
  }, [userDetails, isAuth, requireAuth])

  if (!requireAuth) {
    return (
      <Route
        exact={exact}
        strict={strict}
        path={path}
        component={(props) => (
          <Component {...props} {...(date ? { date } : {})}>
            {children}
          </Component>
        )}
      />
    )
  }

  if (showServerError) {
    return <ServerError />
  }

  // First check authentication if required
  if (requireAuth && !isAuth && !sessionState && !code && !hasLocalToken) {
    // Redirect to login with return URL
    goToLoginPage()
    return <></>
  }

  // If still loading auth state, show loading or nothing
  if (authLoading || !userDetails) {
    return <LoaderView once={true} showLoader={true} />
  }

  // Then check permissions if auth passed or not required
  let hasAccess = true // Default to true if no permissions required

  if (action) {
    hasAccess = hasNavAction(action)
  }

  if (!hasAccess) {
    // Redirect to unauthorized page only if user requested for root page (/ or dashboard)
    const redirectUser = () => {
      const rootPathNames = ['', '/', URL_DASHBOARD]
      if (rootPathNames.find((pathname) => pathname === location.pathname)) {
        return getLandingUrl()
      }

      return URL_UNAUTHORIZED
    }

    return <Redirect to={redirectUser()} state={{ from: location }} replace />
  }

  return requireAuth && fetchFeatureFlagsAction?.state === STATES.success ? (
    <div className={`theme-overrides ${theme}`}>
      {hideNavBar || <Navbar />}
      <main>
        <Route
          exact={exact}
          strict={strict}
          path={path}
          component={
            children
              ? ({ props }) => (
                  <Component {...props} {...(date ? { date } : {})}>
                    {children}
                  </Component>
                )
              : Component
          }
        />
      </main>
    </div>
  ) : (
    <></>
  )
}

const mapStateToProps = (state: RootState) => ({
  featureFlags: state.featureFlags,
  fetchFeatureFlagsAction: getAction(ACTION_FETCH_FEATURE_FLAGS),
  isAuth: state.user.isAuth
})
const mapDispatchToProps = {
  setLanguage,
  setUserDetails,
  fetchFeatureFlags,
  fetchClassificationFlag
}

export default connect(mapStateToProps, mapDispatchToProps)(ProtectedRoute)
